import type { Patient, Reference } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import {
  DIASTOLIC_BLOOD_PRESSURE_CODING,
  SYSTOLIC_BLOOD_PRESSURE_CODING,
} from '@seen/core/models/loinc'
import { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { FirstMatchDecorator } from '#src/components/rich-text-editor/ObservationNode/FirstMatchDecorator.ts'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import {
  CHOOSE_PARTICIPANT,
  isForeignRecord,
} from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import { ObservationVitalInput } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalInput.tsx'
import { ObservationVitalNote } from '#src/components/rich-text-editor/ObservationNode/ObservationVitalNote.tsx'
import type {
  ObservationCaptureTarget,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { withClinicianNote } from '#src/components/rich-text-editor/ObservationNode/vitalNote.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function ObservationNodeBloodPressure({
  attributes,
  updateAttributes,
  participant,
  noParticipant = false,
  editable = true,
  recordStatus,
}: {
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  participant: Reference<Patient> | undefined
  // No participant chosen: nothing can be recorded.
  noParticipant?: boolean
  // Called by the node's record once a first answer is on the node.
  onReferencePersisted?: () => void
  // Whether the note can be changed, and so the vital cleared.
  editable?: boolean
  recordStatus?: ObservationCaptureTarget['recordStatus']
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const record = useNodeRecord()
  const { observation } = record
  // Recorded for someone else than the editor's participant: shown, and
  // clearable, but not changed from here.
  const foreign = isForeignRecord(
    observation,
    noParticipant ? null : participant
  )
  // A note that cannot be changed (signed, posted, read-only) takes no
  // answer here, as every observation control.
  const locked = !editable || isReadOnly || noParticipant || foreign

  // A note written before the vital is recorded, for its record.
  const pendingNote = useRef('')
  const createObservationPromiseRef =
    useRef<Promise<ExtendedObservation> | null>(null)

  const practitioner = useCurrentPractitioner()

  const updateBloodPressure = useCallback(
    async (observation: ExtendedObservation, html: string) => {
      const { diastolic, systolic } = extractBloodPressureFromHtml(html)
      try {
        await record.patch((o) => {
          if (diastolic !== undefined && systolic !== undefined) {
            o.component = [
              {
                code: SYSTOLIC_BLOOD_PRESSURE_CODING,
                valueQuantity: {
                  value: systolic,
                  unit: 'mmHg',
                  system: 'http://unitsofmeasure.org',
                  code: 'mm[Hg]',
                },
              },
              {
                code: DIASTOLIC_BLOOD_PRESSURE_CODING,
                valueQuantity: {
                  value: diastolic,
                  unit: 'mmHg',
                  system: 'http://unitsofmeasure.org',
                  code: 'mm[Hg]',
                },
              },
            ]
          } else {
            o.component = undefined
          }
          o.setNoteFirst(html, practitioner.reference)
        }, observation)
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? `Failed to update blood pressure: ${error.message}`
            : `Failed to update blood pressure`
        toast.error(errorMessage)
        throw error
      }
    },
    [record, practitioner.reference]
  )

  const debouncedOnChange = useDebouncedCallback(async (html) => {
    if (!attributes.reference && !attributes.draft) {
      if (createObservationPromiseRef.current) {
        // Wait for the existing createObservationPromiseRef to finish before proceeding
        const createdObservation = await createObservationPromiseRef.current
        await updateBloodPressure(createdObservation, html)
      } else {
        const { diastolic, systolic } = extractBloodPressureFromHtml(html)
        if (diastolic === undefined || systolic === undefined) {
          toast.error(`Invalid blood pressure values.`)
          return
        }
        createObservationPromiseRef.current = record.create(
          withClinicianNote(
            pendingNote.current,
            practitioner.reference,
            ExtendedObservation.fromComponent({
              component: [
                {
                  code: SYSTOLIC_BLOOD_PRESSURE_CODING,
                  valueQuantity: {
                    value: systolic,
                    unit: 'mmHg',
                    system: 'http://unitsofmeasure.org',
                    code: 'mm[Hg]',
                  },
                },
                {
                  code: DIASTOLIC_BLOOD_PRESSURE_CODING,
                  valueQuantity: {
                    value: diastolic,
                    unit: 'mmHg',
                    system: 'http://unitsofmeasure.org',
                    code: 'mm[Hg]',
                  },
                },
              ],
              code: codeableConcept({
                code: attributes.code,
                system: attributes.system,
                display: attributes.codingDisplay,
              }),
              participant,
              practitioner: practitioner.reference,
              note: html,
              status: recordStatus,
            })
          )
        )
        try {
          await createObservationPromiseRef.current
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? `Failed to submit blood pressure: ${error.message}`
              : `Failed to submit blood pressure`
          toast.error(errorMessage)
          throw error
        } finally {
          createObservationPromiseRef.current = null
        }
      }
    } else {
      invariant(observation, 'observation is required')
      await updateBloodPressure(observation, html)
    }
  }, 500) // Reduced from 2500ms to 500ms to mitigate race condition where users submit timeline notes before observation reference is created. Still debounced to avoid excessive version history entries, and we flush on blur as well.

  const handleAutofocused = useCallback(() => {
    updateAttributes({ autofocus: false })
  }, [updateAttributes])

  return (
    <ObservationNodeFrame
      attributes={attributes}
      label={ExtendedObservation.getNickname(
        attributes.code,
        attributes.codingDisplay
      )}
      className="relative"
      noHover
      // The line itself is the value's editor; a second one in the details
      // would write a value this line does not show.
      valueInDetails={false}
      participant={participant}
      foreign={foreign}
      blockedReason={
        noParticipant && !observation ? CHOOSE_PARTICIPANT : undefined
      }
      updateAttributes={editable ? updateAttributes : undefined}
    >
      <ObservationVitalInput
        code={attributes.code}
        value={observation?.noteFirstText ?? ''}
        debouncedOnChange={debouncedOnChange}
        additionalExtensions={additionalExtensions}
        autofocus={attributes.autofocus}
        onAutofocused={handleAutofocused}
        disabled={locked}
        noValue={!editable && !attributes.reference}
      />
      <ObservationVitalNote
        observation={observation}
        onPendingChange={(note) => {
          pendingNote.current = note
        }}
        disabled={locked}
      />
    </ObservationNodeFrame>
  )
}

const BLOOD_PRESSURE_REGEX = /\b(\d{2,3})\s*\/\s*(\d{2,3})\b/

const additionalExtensions = [
  FirstMatchDecorator.configure({
    regex: BLOOD_PRESSURE_REGEX,
    className: 'font-mono tabular-nums text-foreground',
  }),
]

interface ExtractBloodPressureFromHtmlResult {
  systolic: number | undefined
  diastolic: number | undefined
}

function extractBloodPressureFromHtml(
  html: string
): ExtractBloodPressureFromHtmlResult {
  // Strip HTML tags and normalize whitespace
  const text = html
    .replace(/<[^>]+>/g, ' ') // remove tags
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim()

  const match = text.match(BLOOD_PRESSURE_REGEX)

  if (!match) {
    return { systolic: undefined, diastolic: undefined }
  }

  const systolic = Number(match[1])
  const diastolic = Number(match[2])

  return { systolic, diastolic }
}
